import { CATALOG_SORT, type CatalogSortRole } from 'features/battle/catalog-sort-config';
import { badgeStatsTotal, compareBattleCatalogOrder } from 'features/battle/deck';
import type { BattleOp } from 'features/battle/types';
import {
  BADGE_DESCRIPTIONS,
  BADGE_STAT_KEYS,
  BADGE_STAT_LABELS,
  effectsForBadge,
  isTowerBadge,
  type BadgeStatKey,
  type BadgeStats,
} from 'features/catalog/badge-definitions';
import type { RarityName } from 'features/catalog/rarity-config';

export { CATALOG_SORT } from 'features/battle/catalog-sort-config';
export type { CatalogSortRole } from 'features/battle/catalog-sort-config';

/** Collapsed Gain Prestige: two rows of six, last cell is +more when anything is hidden. */
export const BATTLE_CATALOG_PREVIEW = 11;
/** Collapsed one-row bucket: six cells, last is +more when anything is hidden. */
export const BATTLE_CATALOG_STAT_PREVIEW = 5;

export const BATTLE_CATALOG_SECTIONS = [
  { id: 'prestige', title: 'Gain Prestige' },
  { id: 'finishers', title: 'Strong Finishers' },
  { id: 'towers', title: 'Premium Stats' },
  { id: 'culture', title: BADGE_STAT_LABELS.culture },
  { id: 'engineering', title: BADGE_STAT_LABELS.engineering },
  { id: 'growth', title: BADGE_STAT_LABELS.growth },
  { id: 'fun', title: BADGE_STAT_LABELS.fun },
  { id: 'spotlight', title: BADGE_STAT_LABELS.spotlight },
  { id: 'all', title: 'Your Full Battle Catalog' },
] as const;

export type BattleCatalogSectionId = (typeof BATTLE_CATALOG_SECTIONS)[number]['id'];

export const isBattleCatalogStatSection = (id: BattleCatalogSectionId): id is BadgeStatKey =>
  BADGE_STAT_KEYS.some((key) => key === id);

export const catalogSectionPreview = (id: BattleCatalogSectionId): number => {
  if (id === 'all') return 0;
  if (id === 'prestige') return BATTLE_CATALOG_PREVIEW;
  return BATTLE_CATALOG_STAT_PREVIEW;
};

/**
 * How many claimed badges sit behind +N on a collapsed row. The row is
 * `previewCap + 1` cells; an unclaimed tile takes the last cell, so claimed
 * overflow one sooner instead of wrapping.
 */
export const catalogSectionClaimedOverflow = (
  claimedCount: number,
  unclaimedCount: number,
  previewCap: number
): number => {
  if (previewCap <= 0) return 0;
  const claimedCap = previewCap + (unclaimedCount > 0 ? 0 : 1);
  return claimedCount > claimedCap ? claimedCount - previewCap : 0;
};

/** Named draft rows hide when nothing is claimed or in-deck. The full catalog still lists leftover unclaimed. */
export const catalogSectionHasDraftRow = (
  id: BattleCatalogSectionId,
  claimedCount: number,
  totalCount: number
): boolean => (id === 'all' ? totalCount > 0 : claimedCount > 0);

/**
 * In-deck, other claimed, then solo Unclaimed. Sample-deck cards you have not
 * claimed still sit in `inDeck`, not Unclaimed.
 */
export const splitCatalogSectionBadges = <T extends { id: string }>(
  badges: readonly T[],
  claimedIds: ReadonlySet<string>,
  deckIds: ReadonlySet<string>
): { inDeck: T[]; claimed: T[]; unclaimed: T[] } => {
  const inDeck: T[] = [];
  const claimed: T[] = [];
  const unclaimed: T[] = [];
  for (const badge of badges) {
    if (deckIds.has(badge.id)) inDeck.push(badge);
    else if (claimedIds.has(badge.id)) claimed.push(badge);
    else unclaimed.push(badge);
  }
  return { inDeck, claimed, unclaimed };
};

/**
 * Claimed and in-deck tiles keep the given order. Sample-deck cards you have not
 * claimed still sit in `owned`, not Unclaimed.
 */
export const catalogSectionOwnedInOrder = <T extends { id: string }>(
  badges: readonly T[],
  claimedIds: ReadonlySet<string>,
  deckIds: ReadonlySet<string>
): { owned: T[]; unclaimed: T[] } => {
  const owned: T[] = [];
  const unclaimed: T[] = [];
  for (const badge of badges) {
    if (deckIds.has(badge.id) || claimedIds.has(badge.id)) owned.push(badge);
    else unclaimed.push(badge);
  }
  return { owned, unclaimed };
};

const sectionGroup = (id: BattleCatalogSectionId): 0 | 1 | 2 => {
  if (id === 'all') return 2;
  if (isBattleCatalogStatSection(id)) return 1;
  return 0;
};

/** Raw deck total for this axis. Used to rank sections and pick archetypes. */
const catalogSortStat = (key: BadgeStatKey, totals: Record<BadgeStatKey, number>): number =>
  Math.max(0, totals[key] ?? 0);

/** Deck total × weight, plus the ghost, so every printed point still scores when that axis is empty. */
const catalogFitWeight = (key: BadgeStatKey, totals: Record<BadgeStatKey, number>, weight: number): number =>
  catalogSortStat(key, totals) * weight + CATALOG_SORT.ghost;

const catalogStatWeight = (stat: BadgeStatKey, effectStat: BadgeStatKey | undefined): number =>
  effectStat === stat ? CATALOG_SORT.effectStatWeight : CATALOG_SORT.sideStatWeight;

export type CatalogClaimedSectionStats = Record<BadgeStatKey, { count: number; statTotal: number }>;

export const emptyCatalogClaimedSectionStats = (): CatalogClaimedSectionStats =>
  Object.fromEntries(BADGE_STAT_KEYS.map((key) => [key, { count: 0, statTotal: 0 }])) as CatalogClaimedSectionStats;

/** Claimed (or in-deck) badges per named stat bucket, plus that axis's printed total. */
export const catalogClaimedSectionStats = (
  badges: readonly { id: string; stats: BadgeStats }[],
  claimedIds: ReadonlySet<string>,
  deckIds: ReadonlySet<string> = new Set()
): CatalogClaimedSectionStats => {
  const out = emptyCatalogClaimedSectionStats();
  for (const badge of badges) {
    if (!claimedIds.has(badge.id) && !deckIds.has(badge.id)) continue;
    for (const id of catalogSectionsForBadge(badge.id)) {
      if (!isBattleCatalogStatSection(id)) continue;
      out[id].count += 1;
      out[id].statTotal += badge.stats[id] ?? 0;
    }
  }
  return out;
};

const catalogDeckHasStats = (totals: Record<BadgeStatKey, number>): boolean =>
  BADGE_STAT_KEYS.some((key) => catalogSortStat(key, totals) > 0);

/** Roles first, then stats by deck totals (high → low), then the full catalog. Empty deck uses claimed counts. */
export const compareBattleCatalogSectionOrder = (
  a: BattleCatalogSectionId,
  b: BattleCatalogSectionId,
  totals: Record<BadgeStatKey, number>,
  claimed?: CatalogClaimedSectionStats
): number => {
  const byGroup = sectionGroup(a) - sectionGroup(b);
  if (byGroup) return byGroup;
  if (isBattleCatalogStatSection(a) && isBattleCatalogStatSection(b)) {
    if (catalogDeckHasStats(totals)) {
      return (
        catalogSortStat(b, totals) - catalogSortStat(a, totals) ||
        BADGE_STAT_KEYS.indexOf(a) - BADGE_STAT_KEYS.indexOf(b)
      );
    }
    return (
      (claimed?.[b]?.count ?? 0) - (claimed?.[a]?.count ?? 0) ||
      (claimed?.[b]?.statTotal ?? 0) - (claimed?.[a]?.statTotal ?? 0) ||
      BADGE_STAT_KEYS.indexOf(a) - BADGE_STAT_KEYS.indexOf(b)
    );
  }
  return (
    BATTLE_CATALOG_SECTIONS.findIndex((section) => section.id === a) -
    BATTLE_CATALOG_SECTIONS.findIndex((section) => section.id === b)
  );
};

const pinRoleCatalogSections = (ids: readonly BattleCatalogSectionId[]): BattleCatalogSectionId[] => {
  const present = new Set(ids);
  const roles = BATTLE_CATALOG_SECTIONS.map((section) => section.id).filter(
    (id) => sectionGroup(id) === 0 && present.has(id)
  );
  return [...roles, ...ids.filter((id) => sectionGroup(id) !== 0)];
};

/** Snapshot prefix through this row. Apply keeps that prefix; only later rows live-sort. */
export const battleCatalogSectionFreeze = (
  currentOrder: readonly BattleCatalogSectionId[],
  playedId: BattleCatalogSectionId
): BattleCatalogSectionId[] => {
  const idx = currentOrder.indexOf(playedId);
  return idx < 0 ? [...currentOrder] : currentOrder.slice(0, idx + 1);
};

/**
 * Keep the freeze prefix in place. Only rows below the edited section live-sort.
 * Gain Prestige / Strong Finishers / Premium Stats never reorder as sections.
 */
export const applyBattleCatalogSectionFreeze = (
  liveIds: readonly BattleCatalogSectionId[],
  freeze: readonly BattleCatalogSectionId[]
): BattleCatalogSectionId[] => {
  if (!freeze.length) return pinRoleCatalogSections(liveIds);
  const liveSet = new Set(liveIds);
  const freezeSet = new Set(freeze);
  const kept = freeze.filter((id) => liveSet.has(id));
  const after = liveIds.filter((id) => !freezeSet.has(id));
  return [...kept, ...pinRoleCatalogSections(after)];
};

const gainsOwnPrestige = (who?: 'self' | 'opponent' | 'all') => who !== 'opponent';

/** 4 unlocked piles is the most a filled-board pile payout can print. */
const PILE_PRESTIGE_CAP = 4;
/** Opponent empty piles are a 1–2 engine, not the 4-pile closer cap. */
const EMPTY_OPPONENT_PILE_ENGINE = 2;
/** 1–2 Prestige (or a steal-1 swing) is an engine. 3+, steal-2, empty-deck steal, draw-2-then-activate, a full matching hand swap, Ongoing neighbor-activate Prestige, or Prestige after the board shares a stat is a closer. */
const FINISHER_PRESTIGE = 3;
const FINISHER_DRAW_ACTIVATE = 2;

const prestigeAmountPayoff = (amount: number | 'nonempty_piles' | 'opponent_empty_unlocked_piles'): number => {
  if (typeof amount === 'number') return amount;
  if (amount === 'opponent_empty_unlocked_piles') return EMPTY_OPPONENT_PILE_ENGINE;
  return PILE_PRESTIGE_CAP;
};

const drawnThisOp = (op: BattleOp): number => {
  if (op.kind === 'draw') return op.count * (op.who === 'all' ? 2 : 1);
  if (op.kind === 'draw_up_to_tied' || op.kind === 'draw_up_to') return op.max;
  if (op.kind === 'draw_rarity') return 1;
  return 0;
};

/** Best Prestige this activation can pay you. Steal counts as a 2× race swing. */
export const catalogPrestigePayoff = (ops: readonly BattleOp[], drawn = 0): number => {
  let best = 0;
  let pending = drawn;
  for (const op of ops) {
    if (op.kind === 'if') {
      best = Math.max(
        best,
        catalogPrestigePayoff(op.then, pending),
        catalogPrestigePayoff(op.otherwise ?? [], pending)
      );
      continue;
    }
    pending += drawnThisOp(op);
    if (op.kind === 'gain_prestige_per_drawn') {
      best = Math.max(best, pending);
      continue;
    }
    if (op.kind === 'gain_prestige' && gainsOwnPrestige(op.who)) {
      best = Math.max(best, prestigeAmountPayoff(op.amount));
      continue;
    }
    if (op.kind === 'neighbor_activate_prestige') {
      best = Math.max(best, op.amount);
      continue;
    }
    if (op.kind === 'steal_prestige') {
      best = Math.max(best, op.amount * 2);
      continue;
    }
    if (op.kind === 'guess_drawn_rarity' && op.reward === 'prestige') {
      best = Math.max(best, op.count);
    }
  }
  return best;
};

/** Steal-2 always closes. Steal-1 closes on an empty deck, or when the cost is a skipped next turn. */
const catalogHasCloserSteal = (ops: readonly BattleOp[], emptyDeck = false, skipNext = false): boolean => {
  const skipHere = skipNext || ops.some((op) => op.kind === 'skip_actions_next_turn');
  return ops.some((op) => {
    if (op.kind === 'steal_prestige') return op.amount >= 2 || emptyDeck || skipHere;
    if (op.kind === 'if') {
      const nextEmpty = emptyDeck || op.condition.kind === 'deck_empty';
      return (
        catalogHasCloserSteal(op.then, nextEmpty, skipHere) ||
        catalogHasCloserSteal(op.otherwise ?? [], emptyDeck, skipHere)
      );
    }
    return false;
  });
};

const activatesDrawn = (op: BattleOp): boolean =>
  (op.kind === 'reveal_if' || op.kind === 'reveal_drawn' || op.kind === 'reveal_unique_filter_drawn') &&
  op.play === true;

/** Exchange every matching hand badge — a swing closer. A single random swap is not. */
const catalogHasCloserSwap = (ops: readonly BattleOp[]): boolean =>
  ops.some((op) => {
    if (op.kind === 'swap_hand') return true;
    if (op.kind === 'if') {
      return catalogHasCloserSwap(op.then) || catalogHasCloserSwap(op.otherwise ?? []);
    }
    return false;
  });

/** Ongoing neighbor-activate Prestige snowballs across later Activates — a closer, not a 1-point engine. */
const catalogHasCloserNeighbor = (ops: readonly BattleOp[]): boolean =>
  ops.some((op) => {
    if (op.kind === 'neighbor_activate_prestige') return true;
    if (op.kind === 'if') {
      return catalogHasCloserNeighbor(op.then) || catalogHasCloserNeighbor(op.otherwise ?? []);
    }
    return false;
  });

/** Prestige after the board shares a stat is a closer — you play it when the board is already committed. */
const catalogHasCloserBoardShare = (ops: readonly BattleOp[], sharing = false): boolean =>
  ops.some((op) => {
    if (sharing && op.kind === 'gain_prestige' && gainsOwnPrestige(op.who)) return true;
    if (op.kind === 'if') {
      const nextShare = sharing || op.condition.kind === 'all_board_badges_share_stat';
      return catalogHasCloserBoardShare(op.then, nextShare) || catalogHasCloserBoardShare(op.otherwise ?? [], sharing);
    }
    return false;
  });

/** Draw 2+, then activate one — a combo closer. A single fish-and-activate is an engine. */
const catalogDrawThenActivates = (ops: readonly BattleOp[], drawn = 0): boolean => {
  let pending = drawn;
  for (const op of ops) {
    if (op.kind === 'if') {
      if (catalogDrawThenActivates(op.then, pending) || catalogDrawThenActivates(op.otherwise ?? [], pending)) {
        return true;
      }
      continue;
    }
    pending += drawnThisOp(op);
    if (pending >= FINISHER_DRAW_ACTIVATE && activatesDrawn(op)) return true;
  }
  return false;
};

/** Draft roles this op list actually performs, including steps nested under If. */
export const catalogSectionsForOps = (ops: readonly BattleOp[]): ReadonlySet<BattleCatalogSectionId> => {
  const into = new Set<BattleCatalogSectionId>();
  const payoff = catalogPrestigePayoff(ops);
  if (
    catalogHasCloserSteal(ops) ||
    catalogDrawThenActivates(ops) ||
    catalogHasCloserSwap(ops) ||
    catalogHasCloserNeighbor(ops) ||
    catalogHasCloserBoardShare(ops) ||
    payoff >= FINISHER_PRESTIGE
  ) {
    into.add('finishers');
  } else if (payoff > 0) into.add('prestige');
  return into;
};

const peakStatValue = (stats: BadgeStats): number =>
  BADGE_STAT_KEYS.reduce((peak, key) => Math.max(peak, stats[key]), 0);

/** Towers with a 3+ printed peak. 2/2 commons stay out of Premium Stats. */
const PREMIUM_TOWER_PEAK = 3;

export const isPremiumTowerBadge = (badgeId: string): boolean => {
  if (!isTowerBadge(badgeId)) return false;
  const stats = BADGE_DESCRIPTIONS.get(badgeId)?.stats;
  return stats != null && peakStatValue(stats) >= PREMIUM_TOWER_PEAK;
};

export const catalogSectionsForBadge = (badgeId: string): ReadonlySet<BattleCatalogSectionId> => {
  const effects = effectsForBadge(badgeId);
  const into = new Set(catalogSectionsForOps(effects.flatMap((effect) => effect.ops)));
  if (isTowerBadge(badgeId)) {
    if (isPremiumTowerBadge(badgeId)) into.add('towers');
    into.delete('prestige');
    into.delete('finishers');
  }
  for (const effect of effects) {
    if (effect.stat) into.add(effect.stat);
  }
  into.add('all');
  return into;
};

const printedStatMatchesQuery = (stats: BadgeStats, query: string): boolean =>
  BADGE_STAT_KEYS.some((key) => stats[key] > 0 && BADGE_STAT_LABELS[key].toLowerCase().includes(query));

/** Name, description, effect text, or any printed stat (main or splash). Empty query matches all. */
export const badgeMatchesCatalogSearch = (
  query: string,
  badge: { id: string; name: string; description: string; stats: BadgeStats }
): boolean => {
  const searchQuery = query.trim().toLowerCase();
  if (!searchQuery) return true;
  if (badge.name.toLowerCase().includes(searchQuery)) return true;
  if (badge.description.toLowerCase().includes(searchQuery)) return true;
  if (printedStatMatchesQuery(badge.stats, searchQuery)) return true;
  return effectsForBadge(badge.id).some((effect) => {
    const label = effect.trigger ? `${effect.trigger}: ${effect.text}` : effect.text;
    return label.toLowerCase().includes(searchQuery);
  });
};

/** Effect badges first, towers last; then the usual catalog order for that stat. */
export const compareBattleCatalogStatOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  stat: BadgeStatKey
): number => Number(isTowerBadge(a.id)) - Number(isTowerBadge(b.id)) || compareBattleCatalogOrder(a, b, stat);

/** Printed stats × deck total × side-stat weight. Ghost is omitted so an empty deck still ranks by peak. */
const catalogDeckPull = (stats: BadgeStats, totals: Record<BadgeStatKey, number>): number =>
  BADGE_STAT_KEYS.reduce(
    (sum, stat) => sum + stats[stat] * catalogSortStat(stat, totals) * CATALOG_SORT.sideStatWeight,
    0
  );

/**
 * Premium Stats: deck-weighted pull first, then highest printed stat, then total.
 * A common 5 still ranks above a 4/4 rare when the deck is empty. Rarity does not rank.
 */
export const compareBattleCatalogTowerOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  totals: Record<BadgeStatKey, number>
): number =>
  catalogDeckPull(b.stats, totals) - catalogDeckPull(a.stats, totals) ||
  peakStatValue(b.stats) - peakStatValue(a.stats) ||
  badgeStatsTotal(b.stats) - badgeStatsTotal(a.stats) ||
  (a.name ?? a.id).localeCompare(b.name ?? b.id);

/** The two stats the deck has the most of. Zero totals do not count. */
export const catalogDeckArchetypes = (totals: Record<BadgeStatKey, number>): BadgeStatKey[] =>
  [...BADGE_STAT_KEYS]
    .filter((key) => catalogSortStat(key, totals) > 0)
    .sort(
      (a, b) =>
        catalogSortStat(b, totals) - catalogSortStat(a, totals) ||
        BADGE_STAT_KEYS.indexOf(a) - BADGE_STAT_KEYS.indexOf(b)
    )
    .slice(0, 2);

const effectStatFor = (id: string): BadgeStatKey | undefined =>
  effectsForBadge(id).find((effect) => effect.stat != null)?.stat;

const activateStat = (id: string): BadgeStatKey | undefined =>
  effectsForBadge(id).find((effect) => effect.trigger === 'Activate')?.stat;

/** Printed stats × (deck total + ghost) on every axis. Empty axes still score. */
export const catalogArchetypeFit = (stats: BadgeStats, totals: Record<BadgeStatKey, number>, id?: string): number => {
  const effect = id ? effectStatFor(id) : undefined;
  return BADGE_STAT_KEYS.reduce(
    (sum, stat) => sum + stats[stat] * catalogFitWeight(stat, totals, catalogStatWeight(stat, effect)),
    0
  );
};

const catalogActivateFit = (id: string, totals: Record<BadgeStatKey, number>): number => {
  const stat = activateStat(id);
  return stat ? catalogFitWeight(stat, totals, CATALOG_SORT.effectStatWeight) * CATALOG_SORT.activate : 0;
};

const catalogBadgeFit = (card: { id: string; stats: BadgeStats }, totals: Record<BadgeStatKey, number>): number =>
  catalogArchetypeFit(card.stats, totals, card.id) + catalogActivateFit(card.id, totals);

/**
 * Full catalog: printed stats and activates, each multiplied by that axis's
 * deck total plus the ghost. A first Culture card nudges Culture forward without
 * zeroing Fun and Spotlight. Then the usual catalog order.
 */
export const compareBattleCatalogArchetypeOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  _archetypes: readonly BadgeStatKey[],
  totals: Record<BadgeStatKey, number>
): number => {
  const byFit = catalogBadgeFit(b, totals) - catalogBadgeFit(a, totals);
  if (byFit) return byFit;
  return compareBattleCatalogOrder(a, b, 'all');
};

const prestigePayoffFor = (id: string): number =>
  catalogPrestigePayoff(effectsForBadge(id).flatMap((effect) => effect.ops));

const catalogPayoffWeight = (role: CatalogSortRole): number =>
  role === 'finishers' ? CATALOG_SORT.finisherPayoff : CATALOG_SORT.prestigePayoff;

/** Deck fit plus `payoff × CATALOG_SORT` for this role. */
export const catalogRoleScore = (
  card: { id: string; stats: BadgeStats },
  totals: Record<BadgeStatKey, number>,
  role: CatalogSortRole
): number => catalogBadgeFit(card, totals) + prestigePayoffFor(card.id) * catalogPayoffWeight(role);

/**
 * Gain Prestige / Strong Finishers: deck fit plus a Prestige-payoff bonus.
 * A 1-Prestige card that matches a heavy deck outranks a 2-Prestige splash.
 */
export const compareBattleCatalogRoleOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  archetypes: readonly BadgeStatKey[],
  totals: Record<BadgeStatKey, number>,
  role: CatalogSortRole
): number => {
  const byScore = catalogRoleScore(b, totals, role) - catalogRoleScore(a, totals, role);
  if (byScore) return byScore;
  return compareBattleCatalogArchetypeOrder(a, b, archetypes, totals);
};

/** The clicked badge stays put; every other badge live-sorts as one list around that slot. */
export type BattleCatalogBadgeFreeze = { section: BattleCatalogSectionId; badgeId: string };

/**
 * While a click freeze is on, already-in-deck badges stay at the front and the
 * clicked badge stays in its cell. After the freeze, that badge joins the prefix.
 * An empty deck always live-sorts.
 */
export const catalogSectionSortMode = (
  _id: BattleCatalogSectionId,
  freezeSection: BattleCatalogSectionId | null,
  inDeckCount = 1
): 'live' | 'pivot' => {
  if (!inDeckCount || !freezeSection) return 'live';
  return 'pivot';
};

/**
 * Live-sort the whole list except the clicked badge, then put that badge back in
 * its stored slot. Missing freeze → full live-sort.
 */
export const sortAroundCatalogPivot = <T extends { id: string }>(
  list: readonly T[],
  stored: readonly string[],
  freezeBadgeId: string,
  compare: (a: T, b: T) => number
): T[] => {
  const others = list.filter((item) => item.id !== freezeBadgeId).sort(compare);
  const pivot = list.find((item) => item.id === freezeBadgeId);
  return pinCatalogBadgeSlot(others, pivot, stored);
};

/**
 * Keep `others` in order (already-in-deck prefix first) and drop the clicked
 * badge back into its stored cell.
 */
export const pinCatalogBadgeSlot = <T extends { id: string }>(
  others: readonly T[],
  pivot: T | undefined,
  stored: readonly string[]
): T[] => {
  if (!pivot) return [...others];
  const ids = new Set(others.map((item) => item.id));
  ids.add(pivot.id);
  const slot = stored.filter((id) => ids.has(id)).indexOf(pivot.id);
  if (slot < 0) return [...others, pivot];
  return [...others.slice(0, slot), pivot, ...others.slice(slot)];
};
